// src/components/ReceptionHandoffOverlay.js
import React, { useState, useEffect, useRef } from 'react';

export default function ReceptionHandoffOverlay({ onComplete }) {
  const [isVisible, setIsVisible] = useState(false);
  const [currentPatientIndex, setCurrentPatientIndex] = useState(0);
  const [handoffStatus, setHandoffStatus] = useState('idle');
  const [completedHandoffs, setCompletedHandoffs] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const processingRef = useRef(false);
  const timeoutRefs = useRef([]);

  // Sample interested patients (only warm leads)
  const interestedPatients = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      treatment: 'Routine cleaning',
      reply: 'Yes, I would love to schedule!',
      time: '2 min ago'
    },
    {
      id: 2,
      name: 'Michael Chen',
      treatment: 'Dental checkup',
      reply: 'What time slots are available?',
      time: '5 min ago'
    },
    {
      id: 3,
      name: 'Jessica Davis',
      treatment: 'Teeth whitening',
      reply: 'Yes, I\'ve been meaning to book!',
      time: '8 min ago'
    },
    {
      id: 4,
      name: 'Laura Ng',
      treatment: 'Routine cleaning',
      reply: 'Perfect, I\'ll be there!',
      time: '12 min ago'
    },
    {
      id: 5,
      name: 'Christopher Goh',
      treatment: 'Dental checkup',
      reply: 'Yes please, my teeth need cleaning!',
      time: '15 min ago'
    }
  ];

  // Fade-in animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Process handoffs one by one
  useEffect(() => {
    if (!isVisible || processingRef.current || isComplete) return;
    
    processingRef.current = true;

    const processNextHandoff = () => {
      if (currentPatientIndex >= interestedPatients.length) {
        setIsComplete(true);
        processingRef.current = false;
        const timer = setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
        }, 3300);
        timeoutRefs.current.push(timer);
        return;
      }

      const patient = interestedPatients[currentPatientIndex];
      setHandoffStatus('forwarding');

      const timer1 = setTimeout(() => {
        setHandoffStatus('success');
        setCompletedHandoffs(prev => [...prev, patient]);

        const timer2 = setTimeout(() => {
          setHandoffStatus('idle');
          setCurrentPatientIndex(prev => prev + 1);
          processingRef.current = false;
          const timer3 = setTimeout(() => {
            processNextHandoff();
          }, 533);
          timeoutRefs.current.push(timer3);
        }, 1600);
        timeoutRefs.current.push(timer2);
      }, 2000);
      timeoutRefs.current.push(timer1);
    };

    const startTimer = setTimeout(processNextHandoff, 1066);
    timeoutRefs.current.push(startTimer);

    return () => {
      timeoutRefs.current.forEach(timer => clearTimeout(timer));
      timeoutRefs.current = [];
    };
  }, [isVisible, currentPatientIndex, interestedPatients.length, isComplete]);

  const getStatusIcon = (status) => {
    switch(status) {
      case 'forwarding':
        return '↗';
      case 'success':
        return '✓';
      default:
        return '○';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'forwarding':
        return 'Forwarding to reception...';
      case 'success':
        return '✓ Forwarded Successfully';
      default:
        return 'Waiting...';
    }
  };

  const currentPatient = currentPatientIndex < interestedPatients.length 
    ? interestedPatients[currentPatientIndex] 
    : null;

  return (
    <div style={handoffStyles.overlay}>
      <div style={handoffStyles.glowBg} />
      
      <div 
        style={{
          ...handoffStyles.content,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        }}
      >
        {/* Header */}
        <div style={handoffStyles.header}>
          <div style={handoffStyles.headerLeft}>
            <span style={handoffStyles.headerText}>Handing Off to Your Reception Team</span>
          </div>
          <div style={handoffStyles.headerRight}>
            <span style={handoffStyles.counter}>
              {completedHandoffs.length} / {interestedPatients.length}
            </span>
            <span style={handoffStyles.counterLabel}>Warm Leads Forwarded</span>
          </div>
        </div>

        {/* Stats Summary - No Emojis, Bigger Labels */}
        <div style={handoffStyles.statsContainer}>
          <div style={handoffStyles.statItem}>
            <span style={handoffStyles.statValue}>{completedHandoffs.length}</span>
            <span style={handoffStyles.statLabel}>Forwarded</span>
          </div>
          <div style={handoffStyles.statDivider} />
          <div style={handoffStyles.statItem}>
            <span style={handoffStyles.statValue}>
              {interestedPatients.length - completedHandoffs.length}
            </span>
            <span style={handoffStyles.statLabel}>Pending</span>
          </div>
          <div style={handoffStyles.statDivider} />
          <div style={handoffStyles.statItem}>
            <span style={handoffStyles.statValue}>100%</span>
            <span style={handoffStyles.statLabel}>Qualified</span>
          </div>
        </div>

        {/* Main Handoff Card */}
        <div style={handoffStyles.handoffContainer}>
          {/* Current Patient Card */}
          {currentPatient && !isComplete && (
            <div style={{
              ...handoffStyles.handoffCard,
              animation: handoffStatus !== 'idle' 
                ? 'slideUp 0.5s cubic-bezier(0.22, 1, 0.36, 1)' 
                : 'none',
            }}>
              <div style={handoffStyles.cardHeader}>
                <div style={handoffStyles.patientInfo}>
                  <span style={handoffStyles.patientName}>
                    {currentPatient.name}
                  </span>
                  <span style={handoffStyles.patientTime}>
                    {currentPatient.time}
                  </span>
                </div>
                <div style={{
                  ...handoffStyles.statusBadge,
                  background: handoffStatus === 'success' 
                    ? 'rgba(58, 191, 168, 0.15)' 
                    : 'rgba(59, 130, 246, 0.15)',
                  color: handoffStatus === 'success' 
                    ? '#3ABFA8' 
                    : '#3B82F6',
                }}>
                  <span style={handoffStyles.statusIcon}>
                    {getStatusIcon(handoffStatus)}
                  </span>
                  <span style={handoffStyles.statusText}>
                    {getStatusText(handoffStatus)}
                  </span>
                </div>
              </div>

              <div style={handoffStyles.cardBody}>
                <div style={handoffStyles.detailRow}>
                  <span style={handoffStyles.detailLabel}>Treatment</span>
                  <span style={handoffStyles.detailValue}>
                    {currentPatient.treatment}
                  </span>
                </div>
                <div style={handoffStyles.detailRow}>
                  <span style={handoffStyles.detailLabel}>Reply</span>
                  <span style={handoffStyles.detailValue}>
                    "{currentPatient.reply}"
                  </span>
                </div>
                <div style={handoffStyles.detailRow}>
                  <span style={handoffStyles.detailLabel}>Status</span>
                  <span style={{
                    ...handoffStyles.readyBadge,
                    animation: handoffStatus === 'success' 
                      ? 'pulse 0.8s ease-in-out 2' 
                      : 'none',
                  }}>
                    <span style={handoffStyles.readyDot} />
                    Ready to Book
                  </span>
                </div>
              </div>

              {/* Forwarding Animation */}
              {handoffStatus === 'forwarding' && (
                <div style={handoffStyles.forwardingAnimation}>
                  <div style={handoffStyles.forwardingLine} />
                  <div style={handoffStyles.forwardingPulse} />
                </div>
              )}

              {/* Success Confirmation */}
              {handoffStatus === 'success' && (
                <div style={handoffStyles.successConfirmation}>
                  <span style={handoffStyles.successIcon}>✓</span>
                  <span style={handoffStyles.successText}>
                    Forwarded Successfully
                  </span>
                  <span style={handoffStyles.successSubtext}>
                    Reception notified
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Completed Handoffs List */}
          {completedHandoffs.length > 0 && (
            <div style={handoffStyles.completedList}>
              <div style={handoffStyles.completedHeader}>
                <span style={handoffStyles.completedTitle}>
                  ✓ Forwarded to Reception
                </span>
                <span style={handoffStyles.completedCount}>
                  {completedHandoffs.length} patients
                </span>
              </div>
              <div style={handoffStyles.completedItems}>
                {completedHandoffs.map((patient, index) => (
                  <div 
                    key={patient.id}
                    style={{
                      ...handoffStyles.completedItem,
                      animation: 'slideIn 0.3s ease-out',
                      animationDelay: `${index * 50}ms`,
                    }}
                  >
                    <span style={handoffStyles.completedName}>
                      {patient.name}
                    </span>
                    <span style={handoffStyles.completedTreatment}>
                      {patient.treatment}
                    </span>
                    <span style={handoffStyles.completedCheck}>✓</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Completion Badge */}
        {isComplete && (
          <div style={handoffStyles.completionBadge}>
            <span style={handoffStyles.completionIcon}>✓</span>
            <span style={handoffStyles.completionText}>
              All Warm Leads Forwarded!
            </span>
            <span style={handoffStyles.completionSubtext}>
              {completedHandoffs.length} patients ready for booking
            </span>
          </div>
        )}

        {/* Footer */}
        <div style={handoffStyles.footer}>
          <span style={handoffStyles.footerText}>
            {!isComplete 
              ? 'Automatically forwarding interested patients...' 
              : '✓ All warm leads delivered to reception'}
          </span>
          {!isComplete && (
            <span style={handoffStyles.footerSpeed}>
              Smart Lead Filtering Active
            </span>
          )}
        </div>

        {/* New copy line - positioned right below footer */}
        <div style={handoffStyles.differentiatorCopy}>
          Only patients who are ready to book are forwarded to your reception team.
        </div>
      </div>
    </div>
  );
}

const handoffStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: '#070E16',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: 24,
    overflow: 'hidden',
  },
  glowBg: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 900,
    height: '70%',
    maxHeight: 700,
    background: 'radial-gradient(circle, rgba(45, 155, 138, 0.08) 0%, rgba(45, 155, 138, 0.02) 40%, rgba(45, 155, 138, 0) 70%)',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
    maxWidth: 700,
    width: '100%',
    gap: 16,
  },
  
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0 4px',
    marginLeft: '-20px',
    marginBottom: '8px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  headerText: {
    color: '#FFFFFF',
    fontSize: 32,
    fontWeight: 800,
    letterSpacing: '-0.5px',
    fontFamily: "'Inter', sans-serif",
    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  counter: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-0.5px',
    fontFamily: "'Inter', sans-serif",
  },
  counterLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.3px',
    fontFamily: "'Inter', sans-serif",
  },

  // Stats - Bigger and Bolder
  statsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 32,
    width: '100%',
    padding: '14px 24px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
  },
  statValue: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 700,
    fontFamily: "'Inter', sans-serif",
    letterSpacing: '-0.5px',
  },
  statLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: "'Inter', sans-serif",
  },
  statDivider: {
    width: 1,
    height: 36,
    background: 'rgba(255,255,255,0.06)',
  },

  // Handoff Container
  handoffContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },

  // Handoff Card
  handoffCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: '24px 28px',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  patientInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  patientName: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 600,
    fontFamily: "'Inter', sans-serif",
  },
  patientTime: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 12,
    fontFamily: "'Inter', sans-serif",
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    fontFamily: "'Inter', sans-serif",
  },
  statusIcon: {
    fontSize: 14,
  },
  statusText: {
    fontSize: 13,
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  detailLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "'Inter', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  detailValue: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'right',
    maxWidth: '60%',
    fontFamily: "'Inter', sans-serif",
  },
  readyBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#3ABFA8',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "'Inter', sans-serif",
  },
  readyDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#3ABFA8',
    animation: 'pulse 1.5s ease-in-out infinite',
  },

  // Forwarding Animation
  forwardingAnimation: {
    position: 'relative',
    marginTop: 16,
    height: 2,
    background: 'rgba(59, 130, 246, 0.2)',
    borderRadius: 1,
    overflow: 'hidden',
  },
  forwardingLine: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '30%',
    background: 'linear-gradient(90deg, #3B82F6, #60A5FA)',
    borderRadius: 1,
    animation: 'slideRight 1.2s ease-in-out infinite',
  },
  forwardingPulse: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: 'rgba(59, 130, 246, 0.2)',
    animation: 'pulseRing 1.2s ease-in-out infinite',
  },

  // Success Confirmation
  successConfirmation: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '12px 20px',
    background: 'rgba(58, 191, 168, 0.08)',
    borderRadius: 8,
    border: '1px solid rgba(58, 191, 168, 0.2)',
    animation: 'fadeIn 0.5s ease-out',
  },
  successIcon: {
    color: '#3ABFA8',
    fontSize: 18,
    fontWeight: 700,
  },
  successText: {
    color: '#3ABFA8',
    fontSize: 14,
    fontWeight: 600,
  },
  successSubtext: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    paddingLeft: 8,
    borderLeft: '1px solid rgba(255,255,255,0.1)',
  },

  // Completed List
  completedList: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
    padding: '16px 20px',
  },
  completedHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  completedTitle: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: "'Inter', sans-serif",
  },
  completedCount: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 12,
    fontFamily: "'Inter', sans-serif",
  },
  completedItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    maxHeight: 120,
    overflowY: 'auto',
  },
  completedItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '6px 8px',
    borderRadius: 6,
    background: 'rgba(255,255,255,0.02)',
    transition: 'all 0.3s ease',
  },
  completedName: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    fontWeight: 500,
    flex: 1,
    fontFamily: "'Inter', sans-serif",
  },
  completedTreatment: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    fontFamily: "'Inter', sans-serif",
  },
  completedCheck: {
    color: '#3ABFA8',
    fontSize: 14,
    fontWeight: 700,
  },

  // Completion Badge
  completionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'rgba(45, 155, 138, 0.15)',
    border: '1px solid rgba(45, 155, 138, 0.3)',
    borderRadius: 30,
    padding: '10px 24px',
    animation: 'fadeIn 0.6s ease-out',
  },
  completionIcon: {
    fontSize: 20,
  },
  completionText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: 600,
    fontFamily: "'Inter', sans-serif",
  },
  completionSubtext: {
    color: '#3ABFA8',
    fontSize: 13,
    fontWeight: 500,
    paddingLeft: 12,
    borderLeft: '1px solid rgba(45, 155, 138, 0.3)',
    fontFamily: "'Inter', sans-serif",
  },

  // Footer
  footer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 4px',
  },
  footerText: {
    color: 'rgba(255,255,255,0.25)',
    fontSize: 13,
    letterSpacing: '0.3px',
    fontFamily: "'Inter', sans-serif",
  },
  footerSpeed: {
    color: 'rgba(255,255,255,0.2)',
    fontSize: 12,
    fontFamily: "'JetBrains Mono', monospace",
  },
  
  // New differentiator copy
  differentiatorCopy: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'center',
    letterSpacing: '0.2px',
    maxWidth: '90%',
    marginTop: 4,
    padding: '8px 16px',
    background: 'rgba(45, 155, 138, 0.06)',
    borderRadius: 8,
    border: '1px solid rgba(45, 155, 138, 0.08)',
    fontFamily: "'Inter', sans-serif",
  },
};

// Add keyframes
const handoffStyleSheet = document.createElement("style");
handoffStyleSheet.textContent = `
  @keyframes slideIn {
    0% { opacity: 0; transform: translateX(-10px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideUp {
    0% { opacity: 0; transform: translateY(20px) scale(0.95); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes slideRight {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(400%); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.2); }
  }
  @keyframes pulseRing {
    0%, 100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.3; }
    50% { transform: translate(-50%, -50%) scale(1.5); opacity: 0.8; }
  }
  @keyframes fadeIn {
    0% { opacity: 0; transform: scale(0.95); }
    100% { opacity: 1; transform: scale(1); }
  }
`;
document.head.appendChild(handoffStyleSheet);